import {defineStore} from 'pinia';
import api from '@/lib/api';
import {getSelectedOrganizationId} from '@/lib/organization-selection';
import {isEmailNotVerified} from '@/lib/email-verification';
import {planCurrency, type PlanCurrency} from '@/lib/plan-currency';
import {useAuthStore} from '@/stores/auth';

export type SubscriptionFlavor = 'BASIC' | 'PRO' | 'ENTERPRISE' | 'F_AND_F';

export interface Subscription {
  id: number;
  flavor: SubscriptionFlavor;
  status: 'PENDING' | 'ACTIVE' | 'ON_HOLD' | 'CANCELLED' | 'EXPIRED';
  dodoCustomerId?: string | null;
  dodoSubscriptionId?: string | null;
  dodoProductId?: string | null;
  expiresAt?: string | null;
  cancelAtNextBillingDate: boolean;
  pendingFlavor?: SubscriptionFlavor | null;
}

export interface PlanDetail {
  flavor: SubscriptionFlavor;
  name: string;
  description: string;
  price: string;
  currency: PlanCurrency;
  period: string;
  features: string[];
  highlighted?: boolean;
}

interface State {
  subscription: Subscription | null;
  /** Organization the subscription was fetched for; undefined until fetched. */
  organizationId: string | null | undefined;
  plans: PlanDetail[];
  loading: boolean;
  error: string | null;
}

interface BillingError {
  response?: { status?: number; data?: { detail?: string; message?: string } };
}

// ProblemDetail uses 'detail', legacy uses 'message'
function errorDetail(e: unknown): string | undefined {
  const data = (e as BillingError).response?.data;
  return data?.detail || data?.message;
}

// Bumped whenever the organization or account changes. Requests started before are ignored:
// they write no state, and the actions returning links or results resolve to null.
let generation = 0;

/**
 * Mirrors the backend rule (RunnerMonitoring.entitled) for creating, duplicating, editing and resuming runners
 * and for Monitoring and Metrics.
 */
export function isEntitled(subscription: Subscription | null, now: number): boolean {
  if (subscription?.status !== 'ACTIVE') return false;
  return !subscription.expiresAt || Date.parse(subscription.expiresAt) > now;
}

export const useSubscriptionStore = defineStore('subscription', {
  state: (): State => ({
    subscription: null,
    organizationId: undefined,
    plans: [],
    loading: false,
    error: null,
  }),
  getters: {
    isActive: (state) => state.subscription?.status === 'ACTIVE',
    // A function, so the time is read on every call instead of being cached with the subscription.
    entitledAt:
      (state) =>
      (now: number = Date.now()) =>
        isEntitled(state.subscription, now),
    hasFlavor: (state) => (flavor: string) => state.subscription?.flavor === flavor,
    isPendingSwitch: (state) => state.subscription?.pendingFlavor != null,
    daysUntilRenewal: (state) => {
      if (!state.subscription?.expiresAt) return null;
      const now = new Date();
      const expiry = new Date(state.subscription.expiresAt);
      const diffMs = expiry.getTime() - now.getTime();
      if (diffMs <= 0) return 0;
      return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    },
  },
  actions: {
    /** Drops the state and ignores responses to requests started before, e.g. for another organization. */
    invalidate() {
      generation++;
      this.$reset();
    },
    async fetchSubscription() {
      const context = generation;
      this.loading = true;
      this.error = null;
      const organizationId = getSelectedOrganizationId();
      try {
        const { data } = await api.get<Subscription>('subscription');
        if (context !== generation) return;
        this.subscription = data;
        this.organizationId = organizationId;
      } catch (e) {
        if (context !== generation) return;
        if ((e as BillingError).response?.status === 404) {
          this.subscription = null;
          this.organizationId = organizationId;
        } else if (isEmailNotVerified(e)) {
          // Only an unverified account's default organization without a local plan answers this
          // 403; the plan may still be linked by verifying, so it is not a failure.
          this.subscription = null;
          this.organizationId = organizationId;
          useAuthStore().emailVerified = false;
        } else {
          this.error = 'Failed to fetch subscription';
          throw e;
        }
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async fetchPlans() {
      const context = generation;
      this.loading = true;
      try {
        const { data } = await api.get<PlanDetail[]>('billing/plans', {
          params: { currency: planCurrency() },
        });
        if (context !== generation) return;
        this.plans = data.map(p => ({
          ...p,
          highlighted: p.flavor === 'PRO',
        }));
      } catch (e) {
        if (context !== generation) return;
        this.error = 'Failed to fetch available plans';
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async checkout(flavor: SubscriptionFlavor) {
      const context = generation;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post<{ paymentLink: string }>('billing/checkout', { flavor });
        if (context !== generation) return;
        if (data.paymentLink) {
          window.location.href = data.paymentLink;
        }
      } catch (e) {
        if (context !== generation) return;
        const detail = errorDetail(e);
        if (detail) {
          this.error = detail;
        } else {
          this.error = 'Failed to initiate checkout';
        }
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async getPortalLink() {
      const context = generation;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.get<{ link: string }>('billing/portal');
        return context === generation ? data.link : null;
      } catch (e) {
        if (context !== generation) return null;
        this.error = errorDetail(e) || 'Failed to get billing portal link';
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async getUpdatePaymentMethodLink() {
      const context = generation;
      this.loading = true;
      this.error = null;
      try {
        const returnUrl = window.location.origin + '/profile';
        const { data } = await api.post<{ paymentLink: string }>('billing/update-payment-method', {
          returnUrl,
        });
        return context === generation ? data.paymentLink : null;
      } catch (e) {
        if (context !== generation) return null;
        this.error = errorDetail(e) || 'Failed to get payment method update link';
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
    async switchPlan(flavor: SubscriptionFlavor) {
      const context = generation;
      this.loading = true;
      this.error = null;
      try {
        const { data } = await api.post<{ status: string; downgradeScheduled: boolean }>('billing/switch-plan', { targetFlavor: flavor });
        if (context !== generation) return null;
        if (this.subscription) {
          if (data.downgradeScheduled) {
            this.subscription.pendingFlavor = flavor;
          } else {
            this.subscription.flavor = flavor;
            this.subscription.pendingFlavor = null;
          }
        }
        return data;
      } catch (e) {
        if (context !== generation) return null;
        this.error = errorDetail(e) || 'Failed to switch plan';
        throw e;
      } finally {
        if (context === generation) this.loading = false;
      }
    },
  },
});
